import {
  runContainerFileDropWorkflow,
  runCrossWorkspaceFileDropWorkflow,
  runWorkspaceAddModeWorkflow,
} from "@/action-dialog";
import { noteIntent } from "@/analytics/intent";
import { orgAPI } from "@/api/api";
import { useMoveCanvasInTree, type MoveCanvasInTree } from "@/api/canvas";
import { patchFileRecordCaches } from "@/api/fileRecordCache";
import {
  fileRecordKeys,
  fileRecordListQuery,
  useCopyFileRecord,
  useDeleteFileRecord,
  useMoveFileRecord,
  workspaceTreeQuery,
} from "@/api/fileRecords";
import { useUpdateWorkspaceInboxItem } from "@/api/flow_sessions";
import { useToggleFavorite } from "@/api/resourceTags";
import {
  useFileConflictResolver,
  type IntendedCopy,
  type IntendedMove,
  type ResolvedCopyOperation,
  type ResolvedOperation,
} from "@/components/FileConflictResolver";
import { moveRecordCascading } from "@/pages/workspaces/utils/folderMove";
import {
  trackTransferCompleted,
  trackTransferRejected,
} from "@/utils/analytics";
import type { FileRecord, FileRecordComponents } from "@cohesive/api-client";
import { isCanvasLinkEntity } from "@cohesive/ui-components";
import type { DragCancelEvent, DragEndEvent } from "@dnd-kit/core";
import { useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useCallback, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import type {
  CanvasBoardDropHandler,
  CanvasReparentHandler,
  ChatAttachHandler,
  ComposerAttachTarget,
  WorkspaceDetailDropContext,
} from "./TransferDndProvider";
import { buildWorkspaceMoves, partitionRailFilePayloads } from "./buildIntents";
import { classifyTransfer } from "./classifyTransfer";
import {
  buildWorkspaceDetailDraggable,
  classifyMixedWorkspaceDetailDrop,
  isMixedWorkspaceDetailSelection,
  runWorkspaceDetailMixedDrop,
} from "./handlers/workspaceDetailDrop";
import {
  asDraggableData,
  asDroppableData,
  type DraggableData,
  type DroppableData,
} from "./ids";
import type {
  FileRecordCopyPayload,
  TransferClassification,
  TransferDestination,
} from "./types";

export type TransferAction =
  | "place"
  | "place_chat"
  | "move"
  | "copy"
  | "section_to_folder"
  | "reparent_node"
  | "move_canvas";

export interface TransferDropProps {
  payloadKind: string;
  destinationKind: string;
  surface: string;
  action?: TransferAction;
  count: number;
  isSharedSource?: boolean;
}

function dragCount(draggable: DraggableData): number {
  switch (draggable.kind) {
    case "files":
      return draggable.payloads.length + (draggable.canvasIds?.length ?? 0);
    case "canvas-card":
      return draggable.canvasIds.length;
    case "entity":
      return draggable.entities.length;
    case "skill":
      return draggable.skillIds.length;
    case "integration-connection":
      return draggable.connectionIds.length;
    default:
      return 1;
  }
}

export function transferDropProps(
  draggable: DraggableData,
  droppable: Pick<DroppableData, "surface" | "destination">,
  action?: TransferAction,
): TransferDropProps {
  return {
    payloadKind: draggable.kind,
    destinationKind: droppable.destination.kind,
    surface: droppable.surface,
    action,
    count: dragCount(draggable),
    isSharedSource:
      draggable.kind === "files"
        ? draggable.payloads.some((p) => p.sharedSource)
        : undefined,
  };
}

/**
 * The one event a drop yields. `noop` and `favorite` yield none (Favorite
 * Added is owned by the resource-tag mutation hooks); `invalid` is a
 * `Transfer Rejected`; everything else a `Transfer Completed` whose action
 * is the classification kind.
 */
export function transferDropEvent(
  classification: TransferClassification,
  draggable: DraggableData,
  droppable: Pick<DroppableData, "surface" | "destination">,
): { outcome: "completed" | "rejected"; props: TransferDropProps } | null {
  if (classification.kind === "noop" || classification.kind === "favorite") {
    return null;
  }
  if (classification.kind === "invalid") {
    return {
      outcome: "rejected",
      props: transferDropProps(draggable, droppable),
    };
  }
  return {
    outcome: "completed",
    props: transferDropProps(draggable, droppable, classification.kind),
  };
}

/**
 * One-stop dispatcher for every in-app transfer drop.
 */
export function useTransferDispatcher(opts: {
  getBoardDropHandler: () => CanvasBoardDropHandler | null;
  getReparentHandler: () => CanvasReparentHandler | null;
  getChatAttachHandler: () => ChatAttachHandler | null;
  getComposerAttachTarget: (scopeKey: string) => ComposerAttachTarget | null;
  getWorkspaceDetailContext: () => WorkspaceDetailDropContext | null;
}) {
  const getBoardDropHandlerRef = useRef(opts.getBoardDropHandler);
  getBoardDropHandlerRef.current = opts.getBoardDropHandler;
  const getReparentHandlerRef = useRef(opts.getReparentHandler);
  getReparentHandlerRef.current = opts.getReparentHandler;
  const getChatAttachHandlerRef = useRef(opts.getChatAttachHandler);
  getChatAttachHandlerRef.current = opts.getChatAttachHandler;
  const getComposerAttachTargetRef = useRef(opts.getComposerAttachTarget);
  getComposerAttachTargetRef.current = opts.getComposerAttachTarget;
  const getWorkspaceDetailContextRef = useRef(opts.getWorkspaceDetailContext);
  getWorkspaceDetailContextRef.current = opts.getWorkspaceDetailContext;

  // dnd-kit's `delta` in DragEndEvent accumulates scroll adjustments from
  // its auto-scroller (e.g. when the pointer is near the entity selector's
  // bottom edge during drag activation, it auto-scrolls the selector and
  // subtracts that scroll from delta.y). This corrupts `activatorEvent +
  // delta` as a proxy for the final cursor position. Track the raw pointer
  // position ourselves so onDragEnd always has the true viewport coords.
  const lastPointerRef = useRef({ x: 0, y: 0 });
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      lastPointerRef.current = { x: e.clientX, y: e.clientY };
    };
    // CAPTURE phase: runs before dnd-kit's own pointer handlers, so a
    // `stopPropagation()` in the sensor can't starve us of moves mid-drag —
    // which was leaving the ref frozen at the drag's start (over the left rail)
    // and dropping nodes at the canvas origin. `pointerdown`/`dragover` seed and
    // keep it fresh for touch + native drags too.
    const opts = { passive: true, capture: true } as const;
    document.addEventListener("pointermove", onMove, opts);
    document.addEventListener("pointerdown", onMove, opts);
    document.addEventListener("dragover", onMove as EventListener, opts);
    return () => {
      document.removeEventListener("pointermove", onMove, opts);
      document.removeEventListener("pointerdown", onMove, opts);
      document.removeEventListener("dragover", onMove as EventListener, opts);
    };
  }, []);

  /**
   * The drop's true viewport position. Prefers the live-tracked pointer; if that
   * was never updated (still the `(0,0)` init), falls back to
   * `activatorEvent + delta` so a drop can never silently land at the corner.
   */
  const resolveDropPoint = (event: DragEndEvent) => {
    const p = lastPointerRef.current;
    if (p.x !== 0 || p.y !== 0) return p;
    const act = event.activatorEvent as Partial<PointerEvent> | null;
    if (
      act &&
      typeof act.clientX === "number" &&
      typeof act.clientY === "number"
    ) {
      return { x: act.clientX + event.delta.x, y: act.clientY + event.delta.y };
    }
    return p;
  };

  const queryClient = useQueryClient();
  const { resolveCopies, resolveMoves } = useFileConflictResolver();
  const copyFileRecord = useCopyFileRecord(undefined);
  // Move/delete mutations are workspace-scoped for invalidation. We
  // bind them with `undefined` here and rely on the underlying mutation
  // body to derive the workspace id from the payload.
  const moveFileRecord = useMoveFileRecord(undefined);
  const deleteFileRecord = useDeleteFileRecord(undefined);
  const toggleFavorite = useToggleFavorite();
  const updateInboxItem = useUpdateWorkspaceInboxItem();

  const runCopies = useCallback(
    async (payloads: FileRecordCopyPayload[], dest: TransferDestination) => {
      // Favorites and the pending composer are not copy destinations here
      // (handled upstream — the composer via `attachFilesToSession`); guard
      // so the canvas branch stays well-typed.
      if (dest.kind === "favorites") return;
      if (dest.kind === "composer") return;
      const components: Omit<FileRecordComponents, "path" | "is_folder"> =
        dest.kind === "workspace"
          ? { class: "workspace", workspace_id: dest.workspaceId }
          : dest.kind === "flow_session"
            ? {
                class: "flow-session",
                flow_session_id: dest.flowSessionId,
              }
            : { class: "canvas", canvas_id: dest.canvasId };
      const parentPath =
        dest.kind === "workspace"
          ? dest.parentPath
          : dest.kind === "flow_session"
            ? ""
            : "";

      const copies: IntendedCopy[] = payloads.map((p) => ({
        fileRecordId: p.recordId,
        sourceName: p.name,
        isFolder: p.isFolder,
        source: {
          class: p.sourceClass,
          workspace_id: p.sourceWorkspaceId,
          canvas_id: p.sourceCanvasId,
          flow_session_id: p.sourceFlowSessionId,
          path: p.sourcePath,
          is_folder: p.isFolder || undefined,
        },
        destination: { components, parentPath },
      }));

      const sourceRecords = await collectCopySourceRecords(
        queryClient,
        payloads,
      );

      const ops = await resolveCopies({ copies, sourceRecords });
      if (ops === null) return;
      if (ops.length === 0) return;

      let successes = 0;
      let failures = 0;
      for (const op of ops) {
        try {
          await runCopyOperation(op, copyFileRecord.mutateAsync);
          successes += 1;
        } catch {
          failures += 1;
        }
      }

      const targetLabel =
        dest.kind === "workspace"
          ? dest.parentPath
            ? "folder"
            : "workspace"
          : dest.kind === "flow_session"
            ? "chat"
            : "canvas";
      reportBatchResult({
        verb: "Copied",
        successes,
        failures,
        total: ops.length,
        targetLabel,
      });
    },
    [queryClient, resolveCopies, copyFileRecord.mutateAsync],
  );

  const moveCanvasInTree = useMoveCanvasInTree();

  // Re-file the canvases riding a mixed drag; returns counts for the toast.
  const moveRidingCanvases = useCallback(
    async (
      canvasIds: string[],
      dest: { workspaceId: string; parentPath: string },
    ) => {
      let successes = 0;
      let failures = 0;
      for (const canvasId of canvasIds) {
        try {
          await moveCanvasInTree({
            canvasId,
            parentPath: dest.parentPath,
            workspaceId: dest.workspaceId,
          });
          successes += 1;
        } catch (err) {
          console.error("[TransferDrop] riding canvas move failed", err);
          failures += 1;
        }
      }
      return { successes, failures };
    },
    [moveCanvasInTree],
  );

  const runMoves = useCallback(
    async (
      payloads: FileRecordCopyPayload[],
      dest: Extract<TransferDestination, { kind: "workspace" }>,
      // Canvases riding a mixed drag — moved by this batch AFTER the file
      // conflicts resolve (cancelling the dialog cancels everything), and
      // counted into the ONE toast so "Moved N items" is the whole truth.
      ridingCanvasIds: string[] = [],
    ) => {
      const { moves, rejections } = buildWorkspaceMoves(payloads, {
        workspaceId: dest.workspaceId,
        parentPath: dest.parentPath,
      });

      if (rejections.some((r) => r.kind === "self_folder")) {
        toast.error("Cannot move a folder into itself");
        return;
      }

      // Nothing to do — every payload was a same-parent no-op or out
      // of scope. Stay quiet rather than toasting "moved 0 items" —
      // unless riding canvases still need their move (and their toast).
      if (moves.length === 0) {
        if (ridingCanvasIds.length > 0) {
          const extras = await moveRidingCanvases(ridingCanvasIds, dest);
          reportBatchResult({
            verb: "Moved",
            successes: extras.successes,
            failures: extras.failures,
            total: ridingCanvasIds.length,
            targetLabel: dest.parentPath ? "folder" : "workspace",
          });
        }
        return;
      }

      const sourceRecords = await collectWorkspaceSourceRecords(
        queryClient,
        payloads,
      );
      const sourceRecordMap = new Map(sourceRecords.map((r) => [r.id, r]));

      const intendedMoves: IntendedMove[] = moves.map((m) => ({
        fileRecordId: m.recordId,
        sourceName: m.sourceName,
        destinationParentPath: m.destinationParentPath,
        isFolder: m.isFolder,
      }));

      const ops = await resolveMoves({
        moves: intendedMoves,
        sourceRecords,
        destinationWorkspaceId: dest.workspaceId,
      });
      if (ops === null) return;
      if (ops.length === 0 && ridingCanvasIds.length === 0) return;

      // Riding canvases move only once the conflict dialog (if any) resolved
      // — cancelling it cancels the whole batch, not half of it.
      const extras = await moveRidingCanvases(ridingCanvasIds, dest);

      let successes = 0;
      let failures = 0;
      const moveOne = async (vars: {
        fileRecordId: string;
        payload: import("@cohesive/api-client").MoveFileRecordPayload;
      }) => {
        const moved = await moveFileRecord.mutateAsync(vars);
        patchWorkspaceMoveCaches(queryClient, moved);
        return moved;
      };

      for (const op of ops) {
        try {
          await runMoveOperation(
            op,
            sourceRecordMap,
            sourceRecords,
            moveCanvasInTree,
            moveOne,
            copyFileRecord.mutateAsync,
            deleteFileRecord.mutateAsync,
          );
          successes += 1;
        } catch (err) {
          console.error("[TransferDrop] move op failed", op, err);
          failures += 1;
        }
      }

      invalidateWorkspaceListCaches(queryClient, payloads, dest.workspaceId);

      const targetLabel = dest.parentPath ? "folder" : "workspace";
      reportBatchResult({
        verb: "Moved",
        successes: successes + extras.successes,
        failures: failures + extras.failures,
        total: ops.length + ridingCanvasIds.length,
        targetLabel,
      });
    },
    [
      queryClient,
      resolveMoves,
      moveFileRecord.mutateAsync,
      copyFileRecord.mutateAsync,
      deleteFileRecord.mutateAsync,
      moveRidingCanvases,
    ],
  );

  // Reparent batch: hand off to the canvas page's window-event handler
  // which owns React Flow / Loro coordinates and the auto-slot
  // geometry for inserting children into a target section. The
  // dispatcher here stays storage-agnostic — it just describes the
  // intent, the canvas page applies it.
  const runReparent = useCallback(
    (
      canvasId: string,
      nodeIds: string[],
      targetParentSectionId: string | null,
    ) => {
      if (nodeIds.length === 0) return;
      const handler = getReparentHandlerRef.current();
      if (handler) {
        void Promise.resolve(
          handler({ canvasId, nodeIds, targetParentSectionId }),
        ).catch((err) => {
          console.error("[TransferDrop] reparent handler failed", err);
          toast.error("Failed to move items");
        });
        return;
      }
      window.dispatchEvent(
        new CustomEvent("canvas:reparent-node", {
          detail: { canvasId, nodeIds, targetParentSectionId },
        }),
      );
    },
    [],
  );

  const runSectionToFolder = useCallback(
    async (
      payload: import("./payloads").ExplorerSectionPayload,
      dest: Extract<TransferDestination, { kind: "workspace" }>,
    ) => {
      // Defensive — classifier already short-circuits, but a future
      // caller routing a section drop directly here shouldn't crash.
      if (payload.files.length === 0) {
        toast("Nothing to copy; this section has no files.");
        return;
      }

      const folderName = payload.sectionName.trim() || "Section";
      const folderPath = `${dest.parentPath}${folderName}/`;

      // Mint the folder marker. Reuses the exact call shape from
      // `useWorkspaceUploadQueue.ts`'s `enqueueTree`. On 409 / duplicate
      // (folder already exists at that name) we silently merge — the
      // per-file `resolveCopies` step below handles per-file collisions
      // inside the folder via the standard Replace / Keep both / Skip
      // dialog.
      let folderRecord: FileRecord | null = null;
      try {
        folderRecord = await orgAPI.fileRecords.create({
          provider: "s3",
          destination: {
            class: "workspace",
            workspace_id: dest.workspaceId,
            path: folderPath,
            is_folder: true,
          },
        });
      } catch (err) {
        const status = (err as AxiosError | undefined)?.response?.status;
        if (status !== 409) {
          console.error("[ExplorerDrop] section folder create failed", err);
          toast.error(`Failed to create folder "${folderName}"`);
          return;
        }
        // 409: folder already exists, proceed to merge.
      }

      // Patch the workspace cache so the new folder appears immediately.
      if (folderRecord) patchFileRecordCaches(queryClient, folderRecord);

      // Run the per-file copy through the existing cross-scope pipeline
      // — same conflict resolver UX users already know.
      const copies: IntendedCopy[] = payload.files.map((p) => ({
        fileRecordId: p.recordId,
        sourceName: p.name,
        isFolder: p.isFolder,
        source: {
          class: p.sourceClass,
          workspace_id: p.sourceWorkspaceId,
          canvas_id: p.sourceCanvasId,
          flow_session_id: p.sourceFlowSessionId,
          path: p.sourcePath,
          is_folder: p.isFolder || undefined,
        },
        destination: {
          components: { class: "workspace", workspace_id: dest.workspaceId },
          parentPath: folderPath,
        },
      }));

      const ops = await resolveCopies({ copies, sourceRecords: [] });
      if (ops === null) return;
      if (ops.length === 0) return;

      let successes = 0;
      let failures = 0;
      for (const op of ops) {
        try {
          await runCopyOperation(op, copyFileRecord.mutateAsync);
          successes += 1;
        } catch {
          failures += 1;
        }
      }

      if (failures === 0) {
        toast.success(
          successes === 1
            ? `Copied 1 file into "${folderName}"`
            : `Copied ${successes} files into "${folderName}"`,
        );
      } else if (successes === 0) {
        toast.error(`Failed to copy section into "${folderName}"`);
      } else {
        toast.error(
          `Copied ${successes} of ${ops.length} files into "${folderName}", ${failures} failed`,
        );
      }
    },
    [queryClient, resolveCopies, copyFileRecord.mutateAsync],
  );

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) return;

      /** Copy onto the open canvas board (copy record + mint node at cursor). */
      const invokeCanvasBoardFileDrop = async (
        payloads: FileRecordCopyPayload[],
        opts?: {
          useViewportPlacement?: boolean;
          parentSectionId?: string;
        },
      ): Promise<boolean> => {
        const handler = getBoardDropHandlerRef.current();
        if (!handler) return false;
        try {
          const point = resolveDropPoint(event);
          const accepted = await handler({
            screenX: point.x,
            screenY: point.y,
            payloads,
            ...(opts?.parentSectionId
              ? { parentSectionId: opts.parentSectionId }
              : {}),
            ...(opts?.useViewportPlacement
              ? { useViewportPlacement: true }
              : {}),
          });
          return accepted !== false;
        } catch (err) {
          console.error("[ExplorerDrop] canvas-board handler failed", err);
          toast.error("Failed to drop onto canvas");
          return false;
        }
      };

      const droppable = asDroppableData(over.data.current);
      if (!droppable) return;

      let draggable = asDraggableData(active.data.current);
      const selectionSnapshot = active.data.current?.selectionSnapshot as
        | string[]
        | undefined;
      const detailCtx = getWorkspaceDetailContextRef.current();

      // Mixed workspace-detail selection (files + canvases) only carries
      // `selectionSnapshot` — buildWorkspaceDetailDraggable returns null.
      if (
        selectionSnapshot &&
        detailCtx &&
        isMixedWorkspaceDetailSelection(selectionSnapshot)
      ) {
        const mixedClassification = classifyMixedWorkspaceDetailDrop(
          droppable,
          selectionSnapshot,
        );
        const mixedProps: TransferDropProps = {
          payloadKind: "files",
          destinationKind: droppable.destination.kind,
          surface: droppable.surface,
          count: selectionSnapshot.length,
        };
        if (mixedClassification.kind === "invalid") {
          trackTransferRejected(mixedProps);
          toast.error(mixedClassification.reason);
          return;
        }

        const destination = droppable.destination;
        if (destination.kind !== "workspace") return;
        const destWorkspaceId = destination.workspaceId;
        const targetLabel = mixedClassification.targetLabel;
        noteIntent({ action: "transfer.move", surface: "dnd" });
        void runWorkspaceDetailMixedDrop({
          selectionIds: selectionSnapshot,
          destination,
          sourceWorkspaceId: detailCtx.sourceWorkspaceId,
          canvasMap: detailCtx.canvasMap,
          fileRecordMap: detailCtx.fileRecordMap,
          moveCanvas: moveCanvasInTree,
          moveFileRecord: (vars) => moveFileRecord.mutateAsync(vars),
          copyFileRecord: (vars) => copyFileRecord.mutateAsync(vars),
          deleteFileRecord: (id) => deleteFileRecord.mutateAsync(id),
          resolveMoves,
          targetLabel,
        })
          .then(() => {
            trackTransferCompleted({ ...mixedProps, action: "move" });
            void queryClient.invalidateQueries({
              queryKey: fileRecordKeys.workspace(detailCtx.sourceWorkspaceId),
            });
            if (detailCtx.sourceWorkspaceId !== destWorkspaceId) {
              void queryClient.invalidateQueries({
                queryKey: fileRecordKeys.workspace(destWorkspaceId),
              });
            }
          })
          .catch((err) => {
            console.error("[TransferDrop] mixed workspace drop failed", err);
            toast.error(`Failed to move to ${targetLabel}`);
          });
        return;
      }

      if (!draggable && selectionSnapshot && detailCtx) {
        draggable = buildWorkspaceDetailDraggable(
          selectionSnapshot,
          detailCtx.sourceWorkspaceId,
          detailCtx.canvasMap,
          detailCtx.fileRecordMap,
          detailCtx.currentPath,
        );
      }
      if (!draggable) return;

      const drag = draggable;
      const emitDrop = (c: TransferClassification) => {
        const evt = transferDropEvent(c, drag, droppable);
        if (!evt) return;
        if (evt.outcome === "rejected") trackTransferRejected(evt.props);
        else trackTransferCompleted(evt.props);
      };

      // Canvas card on a canvas surface: place the linked canvases as link
      // cards through the board handler's entity path. The classifier has
      // already refused a pure self-drop; a mixed multi-drag skips the host.
      const placeCanvasCards = (
        drag: Extract<DraggableData, { kind: "canvas-card" }>,
        classification: Extract<TransferClassification, { kind: "place" }>,
        point: {
          screenX: number;
          screenY: number;
          useViewportPlacement?: boolean;
        },
      ) => {
        const handler = getBoardDropHandlerRef.current();
        if (!handler) return;
        const entities = drag.entities.filter(
          (e) =>
            !(isCanvasLinkEntity(e) && e.data.id === classification.canvasId),
        );
        if (entities.length === 0) return;
        void Promise.resolve(
          handler({
            ...point,
            payloads: [],
            entities,
            ...(classification.parentSectionId
              ? { parentSectionId: classification.parentSectionId }
              : {}),
          }),
        )
          .then(() => emitDrop(classification))
          .catch((err) => {
            console.error("[ExplorerDrop] canvas card drop failed", err);
            toast.error("Failed to place canvas on canvas");
          });
      };

      // Canvas dropped on a folder in the workspace tree (rail or workspace
      // page, root included): re-file it there. One request per canvas — the
      // canvas API rewrites its place in the tree, and its workspace too when
      // the drop crosses workspaces.
      if (
        (droppable.surface === "rail-workspace-folder" ||
          droppable.surface === "workspace-folder") &&
        draggable.kind === "canvas-card" &&
        droppable.destination.kind === "workspace"
      ) {
        const dest = droppable.destination;
        // The classifier owns the same-folder no-op rule (the pill shows it).
        const cardClassification = classifyTransfer(draggable, dest);
        if (cardClassification.kind !== "move_canvas") return;

        noteIntent({ action: "transfer.move_canvas", surface: "dnd" });
        const crossWorkspace = draggable.sourceWorkspaceId !== dest.workspaceId;
        void (async () => {
          for (const canvasId of draggable.canvasIds) {
            await moveCanvasInTree({
              canvasId,
              parentPath: dest.parentPath,
              workspaceId: crossWorkspace ? dest.workspaceId : undefined,
            });
          }
          emitDrop(cardClassification);
        })().catch((err) => {
          console.error("[TransferDrop] move canvas failed", err);
          toast.error("Failed to move canvas");
        });
        return;
      }

      const classification = classifyTransfer(draggable, droppable.destination);

      // Files rail drop into a workspace folder. Shared-with-me files always
      // COPY — the recipient can't relocate a file out of the owner's scope
      // without stripping the owner's parent-derived access (the backend
      // enforces this too). Owned files follow the normal partition:
      // workspace-class files move (incl. cross-workspace), cross-class
      // (canvas / flow-session) files copy.
      if (
        droppable.surface === "rail-workspace-folder" &&
        draggable.kind === "files"
      ) {
        if (droppable.destination.kind !== "workspace") return;
        const dest = droppable.destination;
        const sharedPayloads = draggable.payloads.filter((p) => p.sharedSource);
        const ownedPayloads = draggable.payloads.filter((p) => !p.sharedSource);
        const { workspace, crossClassCopy } =
          partitionRailFilePayloads(ownedPayloads);
        const ridingCanvasIds = draggable.canvasIds ?? [];
        const tasks: Promise<void>[] = [];
        if (workspace.length > 0 || ridingCanvasIds.length > 0) {
          tasks.push(runMoves(workspace, dest, ridingCanvasIds));
        }
        if (crossClassCopy.length > 0) {
          tasks.push(runCopies(crossClassCopy, dest));
        }
        if (sharedPayloads.length > 0) {
          tasks.push(runCopies(sharedPayloads, dest));
        }
        if (tasks.length === 0) return;
        const railAction: TransferAction =
          classification.kind === "copy" ? "copy" : "move";
        noteIntent({ action: `transfer.${railAction}`, surface: "dnd" });
        void Promise.all(tasks)
          .then(() =>
            trackTransferCompleted(
              transferDropProps(draggable, droppable, railAction),
            ),
          )
          .catch((err) => {
            console.error("[TransferDrop] rail drop dispatch failed", err);
            toast.error("Failed to drop items");
          });
        return;
      }

      // Copy the dragged records into the session (chat attachments are
      // session-class copies), then hand the created records to `onAttached`
      // so the composer's attachment queue picks them up. Shared by the chat
      // page's `chat-composer` surface and the home/workspace composer's
      // `composer-attach` surface below.
      const attachFilesToSession = async (
        flowSessionId: string,
        files: Extract<DraggableData, { kind: "files" }>,
        onAttached: ChatAttachHandler | null,
      ) => {
        // workspace files attach as live references (the message carries the
        // file's own id; the chat's reference row mints at send) or as
        // explicit session copies — the same reference-or-copy chooser the
        // canvas drop shows, keyed the same way on the destination's
        // workspace. Files living on a canvas / in another chat hit the
        // interception: promote-and-attach (routine) or an independent copy.
        const libraryPayloads = files.payloads.filter(
          (p) => p.sourceClass === "workspace" && !p.isFolder,
        );
        const containerPayloads = files.payloads.filter(
          (p) => p.sourceClass !== "workspace" && !p.isFolder,
        );
        let payloads = files.payloads.filter((p) => p.isFolder);

        const attachDirect: FileRecord[] = [];
        if (libraryPayloads.length > 0) {
          // A standalone chat (no workspace) counts as same-workspace, like a
          // board without one: the routine chooser, not the cross-workspace
          // caveat — there is no other audience to lose access.
          let chatWorkspaceId: string | undefined;
          try {
            const session = await orgAPI.flowSessions.getById(flowSessionId);
            chatWorkspaceId = session.data.flow_session.workspace_id;
          } catch {
            // Unresolvable session — treat as standalone rather than failing
            // the drop.
          }
          const sameWorkspace = libraryPayloads.filter(
            (p) =>
              !chatWorkspaceId ||
              !p.sourceWorkspaceId ||
              p.sourceWorkspaceId === chatWorkspaceId,
          );
          const crossWorkspace = libraryPayloads.filter(
            (p) => !sameWorkspace.includes(p),
          );

          const referencePayloads: typeof libraryPayloads = [];
          if (sameWorkspace.length > 0) {
            const choice = await runWorkspaceAddModeWorkflow({
              count: sameWorkspace.length,
              fileName: sameWorkspace[0]?.name,
              destination: "chat",
            });
            if (choice === "reference") {
              referencePayloads.push(...sameWorkspace);
            } else if (choice === "copy") {
              payloads = [...payloads, ...sameWorkspace];
            }
          }
          if (crossWorkspace.length > 0) {
            const choice = await runCrossWorkspaceFileDropWorkflow({
              count: crossWorkspace.length,
              fileName: crossWorkspace[0]?.name,
              destination: "chat",
            });
            if (choice === "reference") {
              referencePayloads.push(...crossWorkspace);
            } else if (choice === "copy") {
              payloads = [...payloads, ...crossWorkspace];
            }
          }

          if (referencePayloads.length > 0) {
            const records = await collectCopySourceRecords(
              queryClient,
              referencePayloads,
            );
            attachDirect.push(
              ...referencePayloads
                .map((p) => records.find((r) => r.id === p.recordId))
                .filter((r): r is FileRecord => !!r),
            );
          }
        }

        if (containerPayloads.length > 0) {
          const choice = await runContainerFileDropWorkflow({
            count: containerPayloads.length,
            fileName: containerPayloads[0]?.name,
            destination: "chat",
          });
          if (choice === "workspace") {
            for (const p of containerPayloads) {
              try {
                const promoted = await orgAPI.fileRecords.addToWorkspace(
                  p.recordId,
                );
                attachDirect.push(promoted.file_record);
                if (p.sourceCanvasId) {
                  void queryClient.invalidateQueries({
                    queryKey: fileRecordKeys.canvas(p.sourceCanvasId),
                  });
                }
                if (p.sourceFlowSessionId) {
                  void queryClient.invalidateQueries({
                    queryKey: fileRecordKeys.flowSession(p.sourceFlowSessionId),
                  });
                }
                const wsId = promoted.file_record.workspace_id;
                if (wsId) {
                  void queryClient.invalidateQueries({
                    queryKey: fileRecordKeys.workspace(wsId),
                  });
                }
              } catch {
                toast.error(`Failed to add "${p.name}" to the workspace`);
              }
            }
          } else if (choice === "copy") {
            payloads = [...payloads, ...containerPayloads];
          }
        }

        // A workspace folder flattens into the chat: every file inside attaches
        // directly (references, not copies) — the folder itself isn't a thing
        // a chat can hold. Folders ONLY: workspace files whose chooser answer
        // was "copy" also sit in `payloads` now, and without the isFolder
        // guard the prefix match would flatten a file onto itself and attach
        // the reference the user explicitly declined.
        const folderPayloads = payloads.filter(
          (p) =>
            p.isFolder && p.sourceClass === "workspace" && p.sourceWorkspaceId,
        );
        payloads = payloads.filter((p) => !folderPayloads.includes(p));
        if (folderPayloads.length > 0) {
          const scopeRecords = await collectCopySourceRecords(
            queryClient,
            folderPayloads,
          );
          for (const folder of folderPayloads) {
            attachDirect.push(
              ...scopeRecords.filter(
                (r) =>
                  !r.path.endsWith("/") && r.path.startsWith(folder.sourcePath),
              ),
            );
          }
        }

        if (onAttached && attachDirect.length > 0) {
          await onAttached(attachDirect);
          toast.success(
            attachDirect.length === 1
              ? "Added 1 file"
              : `Added ${attachDirect.length} files`,
          );
        }
        if (payloads.length === 0) return;
        const components = {
          class: "flow-session" as const,
          flow_session_id: flowSessionId,
        };
        const copies: IntendedCopy[] = payloads.map((p) => ({
          fileRecordId: p.recordId,
          sourceName: p.name,
          isFolder: p.isFolder,
          source: {
            class: p.sourceClass,
            workspace_id: p.sourceWorkspaceId,
            canvas_id: p.sourceCanvasId,
            flow_session_id: p.sourceFlowSessionId,
            path: p.sourcePath,
            is_folder: p.isFolder || undefined,
          },
          destination: { components, parentPath: "" },
        }));
        const sourceRecords = await collectCopySourceRecords(
          queryClient,
          payloads,
        );
        const ops = await resolveCopies({ copies, sourceRecords });
        if (ops === null || ops.length === 0) return;
        const created: FileRecord[] = [];
        for (const op of ops) {
          try {
            const record = await copyFileRecord.mutateAsync({
              fileRecordId: op.sourceFileRecordId,
              payload: op.payload,
            });
            if (record) created.push(record);
          } catch {
            toast.error("Failed to attach file");
          }
        }
        if (onAttached && created.length > 0) {
          await onAttached(created);
        }
        if (created.length > 0) {
          toast.success(
            created.length === 1
              ? "Attached 1 file"
              : `Attached ${created.length} files`,
          );
        }
      };

      if (
        droppable.surface === "chat-composer" &&
        classification.kind === "copy" &&
        draggable.kind === "files"
      ) {
        const dest = droppable.destination;
        if (dest.kind !== "flow_session") return;
        noteIntent({ action: "transfer.copy", surface: "dnd" });
        void attachFilesToSession(
          dest.flowSessionId,
          draggable,
          getChatAttachHandlerRef.current(),
        )
          .then(() => emitDrop(classification))
          .catch((err) => {
            console.error("[TransferDrop] chat attach failed", err);
            toast.error("Failed to attach files");
          });
        return;
      }

      // Home/workspace composer: the chat may not exist yet. Ask the
      // composer to mint (or return) its flow session — the same auto-create
      // its attach button performs — then run the identical copy+attach.
      if (
        droppable.surface === "composer-attach" &&
        droppable.destination.kind === "composer" &&
        classification.kind === "copy" &&
        draggable.kind === "files"
      ) {
        const target = getComposerAttachTargetRef.current(
          droppable.destination.scopeKey,
        );
        if (!target) return;
        noteIntent({ action: "transfer.copy", surface: "dnd" });
        void (async () => {
          const flowSessionId = await target.ensureSession();
          if (!flowSessionId) {
            // A composer with no session to copy into (the new home starts
            // its canvas at send): workspace files attach as live
            // references, which any chat may carry; nothing else can.
            const library = draggable.payloads.filter(
              (p) => p.sourceClass === "workspace" && !p.isFolder,
            );
            if (library.length === 0) {
              toast.error("Only workspace files can be attached here.");
              return;
            }
            await target.onAttached(
              await collectCopySourceRecords(queryClient, library),
            );
            emitDrop(classification);
            return;
          }
          await attachFilesToSession(
            flowSessionId,
            draggable,
            target.onAttached,
          );
          emitDrop(classification);
        })().catch((err) => {
          console.error("[TransferDrop] composer attach failed", err);
          toast.error("Failed to attach files");
        });
        return;
      }

      // Rail Favorites section: star each dropped row (same effect as
      // the row's Star menu item). The classifier already filtered to
      // favoritable items for this rail's resource type.
      if (droppable.surface === "rail-favorites") {
        if (classification.kind === "invalid") {
          emitDrop(classification);
          return;
        }
        if (classification.kind !== "favorite") return;
        for (const item of classification.items) {
          toggleFavorite.mutate({
            resourceType: item.resourceType,
            resourceId: item.resourceId,
            isFavorite: false,
          });
        }
        return;
      }

      // Canvas explorer scope (canvas rail body or Files-rail context
      // section): the pointer sits over the sidebar, so placement uses
      // the visible viewport — not screen coords on the board.
      if (
        droppable.surface === "canvas-scope" ||
        droppable.surface === "files-rail-canvas-scope"
      ) {
        if (classification.kind === "noop") return;
        if (classification.kind === "invalid") {
          emitDrop(classification);
          return;
        }
        if (draggable.kind === "entity" || draggable.kind === "skill") {
          const handler = getBoardDropHandlerRef.current();
          if (!handler) return;
          void Promise.resolve(
            handler({
              screenX: 0,
              screenY: 0,
              payloads: [],
              entities: draggable.entities,
              useViewportPlacement: true,
            }),
          ).catch((err) => {
            console.error(
              "[ExplorerDrop] canvas-scope entity drop failed",
              err,
            );
            toast.error("Failed to place entity on canvas");
          });
          return;
        }
        if (draggable.kind === "canvas-card") {
          if (classification.kind !== "place") return;
          placeCanvasCards(draggable, classification, {
            screenX: 0,
            screenY: 0,
            useViewportPlacement: true,
          });
          return;
        }
        if (draggable.kind !== "files") return;
        if (classification.kind === "reparent_node") {
          runReparent(
            classification.canvasId,
            classification.nodeIds,
            classification.targetParentSectionId,
          );
          emitDrop(classification);
          return;
        }
        if (classification.kind === "place" || classification.kind === "copy") {
          if (classification.kind === "copy") {
            noteIntent({ action: "transfer.copy", surface: "dnd" });
          }
          void (async () => {
            const placed = await invokeCanvasBoardFileDrop(draggable.payloads, {
              useViewportPlacement: true,
              ...(classification.kind === "place" &&
              classification.parentSectionId
                ? { parentSectionId: classification.parentSectionId }
                : {}),
            });
            if (placed) {
              emitDrop(classification);
              return;
            }
            if (classification.kind === "copy") {
              void runCopies(draggable.payloads, droppable.destination)
                .then(() => emitDrop(classification))
                .catch((err) => {
                  console.error("[ExplorerDrop] copy dispatch failed", err);
                  toast.error("Failed to copy items");
                });
            }
          })();
          return;
        }
        return;
      }

      // Canvas-board: hand off to the registered board handler so
      // CanvasPage can run its `screenToFlowPosition` + `addNode`
      // pipeline against the cursor's screen coordinates at drop.
      // Section drops onto a canvas board are no-ops by classifier
      // rule (handled below) — we forward file payloads, entity
      // payloads, and the chat-session placement variant to the board
      // handler.
      if (droppable.surface === "canvas-board") {
        // Use the real-time tracked pointer position rather than
        // `activatorEvent + delta`. dnd-kit's delta accumulates
        // auto-scroll adjustments (e.g. the selector auto-scrolls while
        // the pointer is near its edge), which shifts the computed drop
        // position away from where the user actually released.
        const { x: screenX, y: screenY } = resolveDropPoint(event);

        // Entity drags are unconditionally `place` on any canvas surface
        // — skip the classifier entirely for them.
        if (draggable.kind === "entity" || draggable.kind === "skill") {
          const handler = getBoardDropHandlerRef.current();
          if (!handler) return;
          void Promise.resolve(
            handler({
              screenX,
              screenY,
              payloads: [],
              entities: draggable.entities,
            }),
          ).catch((err) => {
            console.error(
              "[ExplorerDrop] canvas-board entity drop failed",
              err,
            );
            toast.error("Failed to place entity on canvas");
          });
          trackTransferCompleted(
            transferDropProps(draggable, droppable, "place"),
          );
          return;
        }
        if (classification.kind === "noop") return;
        if (classification.kind === "invalid") {
          emitDrop(classification);
          return;
        }
        if (draggable.kind === "canvas-card") {
          if (classification.kind !== "place") return;
          placeCanvasCards(draggable, classification, { screenX, screenY });
          return;
        }
        const handler = getBoardDropHandlerRef.current();
        if (!handler) return;
        if (
          classification.kind === "place_chat" &&
          draggable.kind === "chat-session"
        ) {
          void Promise.resolve(
            handler({
              screenX,
              screenY,
              payloads: [],
              chatSession: {
                flowSessionId: classification.flowSessionId,
                name: classification.name,
              },
            }),
          ).catch((err) => {
            console.error("[ExplorerDrop] canvas-board chat drop failed", err);
            toast.error("Failed to place chat on canvas");
          });
          emitDrop(classification);
          return;
        }
        if (draggable.kind !== "files") return;
        if (classification.kind === "reparent_node") {
          runReparent(
            classification.canvasId,
            classification.nodeIds,
            classification.targetParentSectionId,
          );
          emitDrop(classification);
          return;
        }
        void invokeCanvasBoardFileDrop(draggable.payloads).then((placed) => {
          if (placed) emitDrop(classification);
        });
        return;
      }

      // Canvas-section: entity drags from the picker always place into
      // the target section — no classification needed.
      if (
        droppable.surface === "canvas-section" &&
        (draggable.kind === "entity" || draggable.kind === "skill")
      ) {
        const handler = getBoardDropHandlerRef.current();
        if (!handler) return;
        const sectionDestination = droppable.destination as Extract<
          typeof droppable.destination,
          { kind: "canvas_section" }
        >;
        void Promise.resolve(
          handler({
            screenX: 0,
            screenY: 0,
            payloads: [],
            parentSectionId: sectionDestination.sectionId,
            entities: draggable.entities,
          }),
        ).catch((err) => {
          console.error(
            "[ExplorerDrop] canvas-section entity drop failed",
            err,
          );
          toast.error("Failed to place entity into section");
        });
        trackTransferCompleted(
          transferDropProps(draggable, droppable, "place"),
        );
        return;
      }

      // Canvas-section: a `place` classification (some payloads are
      // unplaced canvas files dropped on a section row) or a
      // `place_chat` (chat row dropped on a section) routes to the
      // same board handler as canvas-board drops, with the target
      // section threaded through. The handler computes its own flow
      // drop position from the section's interior origin — the
      // cursor sits in the explorer pane and isn't a useful anchor
      // for nodes that should land inside the section. Reusing the
      // board handler keeps `layoutGridAroundCursor` as the single
      // source of truth for multi-file grid layout.
      if (
        droppable.surface === "canvas-section" &&
        classification.kind === "place_chat" &&
        draggable.kind === "chat-session"
      ) {
        const handler = getBoardDropHandlerRef.current();
        if (!handler) return;
        void Promise.resolve(
          handler({
            screenX: 0,
            screenY: 0,
            payloads: [],
            parentSectionId: classification.parentSectionId ?? undefined,
            chatSession: {
              flowSessionId: classification.flowSessionId,
              name: classification.name,
            },
          }),
        ).catch((err) => {
          console.error("[ExplorerDrop] canvas-section chat drop failed", err);
          toast.error("Failed to place chat into section");
        });
        emitDrop(classification);
        return;
      }
      if (
        droppable.surface === "canvas-section" &&
        draggable.kind === "canvas-card"
      ) {
        if (classification.kind === "invalid") {
          emitDrop(classification);
          return;
        }
        if (classification.kind !== "place") return;
        placeCanvasCards(draggable, classification, { screenX: 0, screenY: 0 });
        return;
      }
      if (
        droppable.surface === "canvas-section" &&
        classification.kind === "place" &&
        draggable.kind === "files"
      ) {
        const handler = getBoardDropHandlerRef.current();
        if (!handler) return;
        void Promise.resolve(
          handler({
            // The handler ignores screenX/screenY when
            // `parentSectionId` is set, but the field is required by
            // the type — use the cursor as a safe fallback.
            screenX: 0,
            screenY: 0,
            payloads: draggable.payloads,
            parentSectionId: classification.parentSectionId ?? undefined,
          }),
        ).catch((err) => {
          console.error("[ExplorerDrop] canvas-section place failed", err);
          toast.error("Failed to drop into section");
        });
        emitDrop(classification);
        return;
      }

      switch (classification.kind) {
        case "noop":
        // "place" / "place_chat" only carry semantic meaning when
        // dropped on the canvas board (or a section, handled above).
        // On the canvas-scope explorer body we treat them as no-ops
        // — initial placement happens via the board's drop handler.
        case "place":
        case "place_chat":
          return;
        case "invalid":
          emitDrop(classification);
          return;
        case "move": {
          if (draggable.kind !== "files") return;
          const moveDest = {
            kind: "workspace" as const,
            workspaceId: classification.workspaceId,
            parentPath: classification.parentPath,
          };
          // Shared-with-me files COPY (the recipient can't relocate a file out
          // of the owner's scope; the backend also rejects the move) — owned
          // files move. Same rule as the Files-rail surface, applied here so the
          // workspace-detail `workspace-folder` surface behaves consistently.
          const sharedPayloads = draggable.payloads.filter(
            (p) => p.sharedSource,
          );
          const ownedPayloads = draggable.payloads.filter(
            (p) => !p.sharedSource,
          );
          const ridingCanvasIds = draggable.canvasIds ?? [];
          const moveTasks: Promise<void>[] = [];
          if (ownedPayloads.length > 0 || ridingCanvasIds.length > 0) {
            moveTasks.push(runMoves(ownedPayloads, moveDest, ridingCanvasIds));
          }
          if (sharedPayloads.length > 0) {
            moveTasks.push(runCopies(sharedPayloads, moveDest));
          }
          if (moveTasks.length === 0) return;
          noteIntent({ action: "transfer.move", surface: "dnd" });
          void Promise.all(moveTasks)
            .then(() => emitDrop(classification))
            .catch((err) => {
              console.error("[ExplorerDrop] move dispatch failed", err);
              toast.error("Failed to move items");
            });
          return;
        }
        case "copy":
          if (draggable.kind !== "files") return;
          noteIntent({ action: "transfer.copy", surface: "dnd" });
          // Workspace / cross-canvas files dropped on a canvas destination
          // should copy into canvas scope *and* place a node when the board
          // is mounted. `canvas-scope` (explorer body) previously fell
          // through to `runCopies`, which only pinned into the file volume.
          if (droppable.destination.kind === "canvas") {
            void (async () => {
              const placed = await invokeCanvasBoardFileDrop(
                draggable.payloads,
              );
              if (placed) {
                emitDrop(classification);
                return;
              }
              void runCopies(draggable.payloads, droppable.destination)
                .then(() => emitDrop(classification))
                .catch((err) => {
                  console.error("[ExplorerDrop] copy dispatch failed", err);
                  toast.error("Failed to copy items");
                });
            })();
            return;
          }
          void runCopies(draggable.payloads, droppable.destination)
            .then(() => emitDrop(classification))
            .catch((err) => {
              console.error("[ExplorerDrop] copy dispatch failed", err);
              toast.error("Failed to copy items");
            });
          return;
        case "section_to_folder":
          if (draggable.kind !== "section") return;
          noteIntent({ action: "transfer.section_to_folder", surface: "dnd" });
          void runSectionToFolder(draggable.payload, {
            kind: "workspace",
            workspaceId: classification.workspaceId,
            parentPath: classification.parentPath,
          })
            .then(() => emitDrop(classification))
            .catch((err) => {
              console.error("[ExplorerDrop] section dispatch failed", err);
              toast.error("Failed to copy section");
            });
          return;
        case "reparent_node":
          // Same-canvas reorganization. The canvas page owns the
          // geometry math (auto-slot inside the target section, or
          // keep world position when unparenting) and applies it via
          // `canvas.batchUpdateNodeParents`.
          runReparent(
            classification.canvasId,
            classification.nodeIds,
            classification.targetParentSectionId,
          );
          emitDrop(classification);
          return;
      }
    },
    // `opts.getBoardDropHandler` is read via `getBoardDropHandlerRef`
    // (kept current above) so it deliberately stays out of the dep
    // array — listing the inline `opts` literal here would bust this
    // memo on every parent render.
    [
      runMoves,
      runCopies,
      runSectionToFolder,
      runReparent,
      resolveMoves,
      resolveCopies,
      queryClient,
      copyFileRecord.mutateAsync,
      moveFileRecord.mutateAsync,
      deleteFileRecord.mutateAsync,
      moveCanvasInTree,
      toggleFavorite.mutate,
      updateInboxItem.mutate,
    ],
  );

  const onDragCancel = useCallback((_event: DragCancelEvent) => {
    // No state to reset — overlay unmounts on cancel automatically.
  }, []);

  return { onDragEnd, onDragCancel };
}

/** @deprecated Use `useTransferDispatcher`. */
export function useExplorerDropDispatcher(opts: {
  getBoardDropHandler: () => CanvasBoardDropHandler | null;
}) {
  return useTransferDispatcher({
    getBoardDropHandler: opts.getBoardDropHandler,
    getReparentHandler: () => null,
    getChatAttachHandler: () => null,
    getComposerAttachTarget: () => null,
    getWorkspaceDetailContext: () => null,
  });
}

async function runCopyOperation(
  op: ResolvedCopyOperation,
  copyMutation: ReturnType<typeof useCopyFileRecord>["mutateAsync"],
): Promise<void> {
  await copyMutation({
    fileRecordId: op.sourceFileRecordId,
    payload: op.payload,
  });
}

async function getWorkspaceSourceRecords(
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: string,
): Promise<FileRecord[]> {
  return queryClient.fetchQuery({
    ...workspaceTreeQuery(workspaceId),
    staleTime: Infinity,
  });
}

function getCanvasSourceRecords(
  queryClient: ReturnType<typeof useQueryClient>,
  canvasId: string,
): Promise<FileRecord[]> {
  return queryClient.fetchQuery({
    ...fileRecordListQuery({ class: "canvas", canvas_id: canvasId }),
    staleTime: Infinity,
  });
}

function getFlowSessionSourceRecords(
  queryClient: ReturnType<typeof useQueryClient>,
  flowSessionId: string,
): Promise<FileRecord[]> {
  return queryClient.fetchQuery({
    ...fileRecordListQuery({
      class: "flow-session",
      flow_session_id: flowSessionId,
    }),
    staleTime: Infinity,
  });
}

/** Source listings for copy/merge resolution — all dragged scopes. */
async function collectCopySourceRecords(
  queryClient: ReturnType<typeof useQueryClient>,
  payloads: FileRecordCopyPayload[],
): Promise<FileRecord[]> {
  const seen = new Set<string>();
  const records: FileRecord[] = [];
  for (const p of payloads) {
    let scopeKey: string | null = null;
    let fetchRecords: (() => Promise<FileRecord[]>) | null = null;

    if (p.sourceClass === "workspace" && p.sourceWorkspaceId) {
      scopeKey = `workspace:${p.sourceWorkspaceId}`;
      fetchRecords = () =>
        getWorkspaceSourceRecords(queryClient, p.sourceWorkspaceId!);
    } else if (p.sourceClass === "canvas" && p.sourceCanvasId) {
      scopeKey = `canvas:${p.sourceCanvasId}`;
      fetchRecords = () =>
        getCanvasSourceRecords(queryClient, p.sourceCanvasId!);
    } else if (p.sourceClass === "flow-session" && p.sourceFlowSessionId) {
      scopeKey = `flow-session:${p.sourceFlowSessionId}`;
      fetchRecords = () =>
        getFlowSessionSourceRecords(queryClient, p.sourceFlowSessionId!);
    }

    if (!scopeKey || !fetchRecords || seen.has(scopeKey)) continue;
    seen.add(scopeKey);
    try {
      const list = await fetchRecords();
      records.push(...list);
    } catch {
      // best-effort; missing source records only block folder merges,
      // not plain copies.
    }
  }
  return records;
}

async function collectWorkspaceSourceRecords(
  queryClient: ReturnType<typeof useQueryClient>,
  payloads: FileRecordCopyPayload[],
): Promise<FileRecord[]> {
  const seen = new Set<string>();
  const records: FileRecord[] = [];
  for (const p of payloads) {
    if (p.sourceClass !== "workspace" || !p.sourceWorkspaceId) continue;
    if (seen.has(p.sourceWorkspaceId)) continue;
    seen.add(p.sourceWorkspaceId);
    try {
      const list = await getWorkspaceSourceRecords(
        queryClient,
        p.sourceWorkspaceId,
      );
      records.push(...list);
    } catch {
      // best-effort; missing source records only block folder merges,
      // not plain copies.
    }
  }
  return records;
}

async function runMoveOperation(
  op: ResolvedOperation,
  sourceRecordMap: Map<string, FileRecord>,
  allRecords: FileRecord[],
  moveCanvas: MoveCanvasInTree,
  moveMutation: (vars: {
    fileRecordId: string;
    payload: import("@cohesive/api-client").MoveFileRecordPayload;
  }) => Promise<FileRecord>,
  copyMutation: ReturnType<typeof useCopyFileRecord>["mutateAsync"],
  deleteMutation: ReturnType<typeof useDeleteFileRecord>["mutateAsync"],
): Promise<void> {
  if (op.kind === "move") {
    const sourceRecord = sourceRecordMap.get(op.fileRecordId);
    if (!sourceRecord) {
      await moveMutation({
        fileRecordId: op.fileRecordId,
        payload: op.payload,
      });
      return;
    }
    await moveRecordCascading({
      record: sourceRecord,
      payload: op.payload,
      allRecords,
      moveOne: moveMutation,
      moveCanvas,
    });
    return;
  }
  if (op.kind === "move_canvas") {
    await moveCanvas({
      canvasId: op.canvasId,
      parentPath: op.destinationParentPath,
      workspaceId: op.workspaceId,
    });
    return;
  }
  if (op.kind === "replace_contents") {
    await copyMutation({
      fileRecordId: op.sourceFileRecordId,
      payload: op.payload,
    });
    return;
  }
  await deleteMutation(op.fileRecordId);
}

function patchWorkspaceMoveCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  movedRecord: FileRecord,
): void {
  if (!movedRecord.workspace_id || movedRecord.class !== "workspace") return;
  patchFileRecordCaches(queryClient, movedRecord);
}

function invalidateWorkspaceListCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  payloads: FileRecordCopyPayload[],
  destWorkspaceId: string,
): void {
  const workspaceIds = new Set<string>([destWorkspaceId]);
  for (const p of payloads) {
    if (p.sourceClass === "workspace" && p.sourceWorkspaceId) {
      workspaceIds.add(p.sourceWorkspaceId);
    }
  }
  for (const id of workspaceIds) {
    void queryClient.invalidateQueries({
      queryKey: fileRecordKeys.workspace(id),
    });
  }
}

function reportBatchResult({
  verb,
  successes,
  failures,
  total,
  targetLabel,
}: {
  verb: "Moved" | "Copied";
  successes: number;
  failures: number;
  total: number;
  targetLabel: string;
}): void {
  if (failures === 0) {
    toast.success(
      successes === 1
        ? `${verb} 1 item to ${targetLabel}`
        : `${verb} ${successes} items to ${targetLabel}`,
    );
    return;
  }
  if (successes === 0) {
    // Past-tense `verb` ("Moved" / "Copied") reads correctly in the
    // success toast, but "Failed to" needs the base form. Map
    // explicitly so future verbs added to the union force a
    // matching base-form entry instead of a silent grammar bug.
    const baseVerb = verb === "Moved" ? "move" : "copy";
    toast.error(
      total === 1
        ? `Failed to ${baseVerb} item`
        : `Failed to ${baseVerb} ${total} items`,
    );
    return;
  }
  toast.error(`${verb} ${successes} of ${total} items, ${failures} failed`);
}
